import { useMemo, useState } from 'react';
import { getAllOrgUnitsFromGoogleQuery } from '../api/users-api';
import { useQuery } from '@tanstack/react-query';
import Autocomplete from '@mui/material/Autocomplete';
import CircularProgress from '@mui/material/CircularProgress';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
interface OrganizationsAutoCompleteProps {
  autoCompleteValue: string;
  setAutoCompleteValue: (value: string) => void;
  size?: 'small' | 'medium';
  disabled?: boolean;
  hasError?: boolean;
  newKey?: string;
  helperText?: string;
  onSelection?: (value: string) => void;
  autoFocus?: boolean;
  disableParents?: boolean;
}

export default function OrganizationsAutoComplete({
  autoCompleteValue,
  setAutoCompleteValue,
  size,
  disabled,
  hasError,
  newKey,
  helperText,
  onSelection,
  autoFocus,
  disableParents,
}: OrganizationsAutoCompleteProps) {
  const [inputValue, setInputValue] = useState('');

  const key = useMemo(
    () => newKey || new Date().getTime().toString(),
    [newKey]
  );

  const { data: orgUnits, isLoading } = useQuery(
    getAllOrgUnitsFromGoogleQuery()
  );

  const parentIdentifiers = useMemo(() => {
    const parents = new Set<string>();
    if (orgUnits) {
      orgUnits.forEach((ou) => {
        if (ou.parentOrgUnitId) parents.add(ou.parentOrgUnitId);
        if (ou.parentOrgUnitPath) parents.add(ou.parentOrgUnitPath);
      });
    }
    return parents;
  }, [orgUnits]);

  return (
    <Autocomplete
      key={key}
      id="org-units-autocomplete"
      disabled={disabled || isLoading}
      componentsProps={{
        paper: {
          sx: {
            minWidth: 400,
          },
        },
      }}
      value={
        orgUnits?.find((ou) => ou.orgUnitPath === autoCompleteValue) || null
      }
      onChange={(_event, newValue, reason) => {
        let selectedValue = '';
        if (reason === 'clear') {
          setAutoCompleteValue('');
          if (onSelection) {
            onSelection('');
          }
          return;
        }
        selectedValue = newValue?.orgUnitPath || '';
        setAutoCompleteValue(selectedValue);
        if (
          selectedValue &&
          (reason === 'selectOption' || reason === 'createOption') &&
          onSelection
        ) {
          onSelection(selectedValue);
        }
      }}
      getOptionLabel={(option) => option.orgUnitPath}
      getOptionDisabled={(option) => {
        if (!disableParents || !orgUnits) {
          return false;
        }
        return (
          parentIdentifiers.has(option.orgUnitId) ||
          parentIdentifiers.has(option.orgUnitPath)
        );
      }}
      options={
        orgUnits?.sort((a, b) => a.orgUnitPath.localeCompare(b.orgUnitPath)) ||
        []
      }
      renderOption={(props, option) => {
        const isParent =
          parentIdentifiers.has(option.orgUnitId) ||
          parentIdentifiers.has(option.orgUnitPath);

        const isDisabled = disableParents && isParent;

        if (isDisabled) {
          const { key, ...otherProps } = props;
          return (
            <Tooltip
              key={key || option.orgUnitId}
              title="Cannot export a parent organizational unit."
              placement="right"
              arrow
            >
              <span style={{ display: 'block', width: '100%' }}>
                <li {...otherProps} style={{ pointerEvents: 'auto' }}>
                  {option.orgUnitPath}
                </li>
              </span>
            </Tooltip>
          );
        }

        const { key, ...optionProps } = props;
        return (
          <li key={key} {...optionProps}>
            {option.orgUnitPath}
          </li>
        );
      }}
      size={size}
      autoComplete
      autoHighlight
      noOptionsText="No OU's Found"
      renderInput={(params) => (
        <TextField
          {...params}
          autoFocus={autoFocus}
          label="Search for an organizational unit"
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {isLoading ? (
                  <CircularProgress color="inherit" size={20} />
                ) : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
          fullWidth
          error={hasError}
          helperText={helperText}
        />
      )}
      inputValue={inputValue}
      onInputChange={(_event, newInputValue) => {
        setInputValue(newInputValue);
      }}
    />
  );
}
